'use client'

import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { api } from '@/lib/api'
import { getTenantCacheKey } from '@/lib/tenantCache'

interface Barber {
  id: string
  first_name: string
  last_name: string
  photo_url?: string
  bio?: string
  specialty?: string
  rating_avg: string | number
  rating_count: number
  is_featured: boolean
  is_available: boolean
  user?: {
    first_name: string
    last_name: string
    email: string
    phone: string
  }
}

interface BarbersContextType {
  barbers: Barber[]
  loading: boolean
  error: Error | null
  refetch: () => Promise<void>
}

const BarbersContext = createContext<BarbersContextType | undefined>(undefined)

const BARBERS_CACHE_KEY = 'barbers_cache'
const CACHE_DURATION = 10 * 60 * 1000 // 10 minutos

export function BarbersProvider({ children, skipFetch = false }: { children: ReactNode; skipFetch?: boolean }) {
  const [barbers, setBarbers] = useState<Barber[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const loadBarbers = async () => {
    try {
      setLoading(true)
      setError(null)

      // Intentar cargar desde caché (SSR safe)
      if (typeof window !== 'undefined') {
        const cached = localStorage.getItem(getTenantCacheKey(BARBERS_CACHE_KEY))
        if (cached) {
          try {
            const { data, timestamp } = JSON.parse(cached)
            const now = Date.now()
            
            if (now - timestamp < CACHE_DURATION) {
              setBarbers(data)
              setLoading(false)
              // Actualizar en background solo si no estamos en admin
              if (!skipFetch) {
                api.get('/public/barbers').then(freshData => {
                  setBarbers(Array.isArray(freshData) ? freshData : [])
                  localStorage.setItem(getTenantCacheKey(BARBERS_CACHE_KEY), JSON.stringify({
                    data: freshData,
                    timestamp: Date.now()
                  }))
                }).catch(() => {})
              }
              return
            }
          } catch (e) {
            localStorage.removeItem(getTenantCacheKey(BARBERS_CACHE_KEY))
          }
        }
      }

      // If skipFetch and we have no cache, don't fetch
      if (skipFetch) {
        setLoading(false)
        return
      }

      // Cargar desde API
      const data = await api.get('/public/barbers')
      // Normalizar: asegurar que cada barber tenga .user (compat mock)
      const barbersData = (Array.isArray(data) ? data : []).map((b: any) => ({
        ...b,
        user: b.user || {
          first_name: b.first_name || b.name?.split(' ')[0] || '',
          last_name: b.last_name || b.name?.split(' ').slice(1).join(' ') || '',
          email: b.email || '',
          phone: b.phone || '',
        },
      }))
      setBarbers(barbersData)
      
      // Guardar en caché (solo en cliente)
      if (typeof window !== 'undefined') {
        localStorage.setItem(getTenantCacheKey(BARBERS_CACHE_KEY), JSON.stringify({
          data: barbersData,
          timestamp: Date.now()
        }))
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Error loading barbers'))
      console.error('Error loading barbers:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadBarbers()
  }, [])

  const refetch = async () => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(getTenantCacheKey(BARBERS_CACHE_KEY))
    }
    await loadBarbers()
  }

  return (
    <BarbersContext.Provider value={{ barbers, loading, error, refetch }}>
      {children}
    </BarbersContext.Provider>
  )
}

export function useBarbers() {
  const context = useContext(BarbersContext)
  if (context === undefined) {
    throw new Error('useBarbers must be used within a BarbersProvider')
  }
  return context
}
